import { useEffect } from 'react';
import type { ReactNode } from 'react';
import Helmet from 'react-helmet';
import { useParams } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import {
  RELATION_OWNED_BY,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  EntityRefLinks,
  getEntityRelations,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import type { TechDocsEntityMetadata } from '@backstage/plugin-techdocs-react';
import { ButtonLink, Skeleton } from '@backstage/ui';
import type { HeaderBreadcrumb, HeaderMetadataItem } from '@backstage/ui';
import { EN_DASH } from '@internal/backstage-plugin-neo4j-app-common';
import {
  PluginPage,
  Timestamp,
} from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { RiCodeLine, RiExternalLinkLine } from '@remixicon/react';
import { createTechdocsRootRef } from '../routes';
import { DocsBackLink } from './DocsBackLink';
import { DocsLayout } from './DocsLayout';
import {
  DOC_TYPES,
  DOCS_LAST_UPDATE_ANNOTATION,
  LENGTH_ANNOTATION,
} from '../types';
import { useReaderDocType } from '../hooks/useReaderDocType';
import { useReaderEntity } from '../hooks/useReaderEntity';
import { useSubDocEntity } from '../hooks/useSubDocEntity';
import { useLegacyDocsLocation } from '../hooks/useLegacyDocsLocation';
import { useLegacyDocsNav } from '../hooks/useLegacyDocsNav';
import {
  isCategoryDocEntity,
  isLegacyDocsEntity,
  LEGACY_DOCS_TITLE,
  resolveDocType,
} from '../utils/docCategory';
import { parseDocsUpdateDate } from '../utils/parseDocsUpdateDate';
import { getCategoryPath } from '../utils/docsView';
import { findNavTrail, getFirstPagePath } from '../utils/legacyDocsNav';
import type { NavNode } from '../utils/parseMkdocsNav';

type ReaderPageFrameProps = {
  children: ReactNode;
};

/**
 * The page frame of the docs reader: ui-kit `PluginPage` with the header of
 * the open doc, the docs sidebar, and a back link above the doc outside the
 * legacy site. The frame stays mounted while the doc loads and when it does
 * not exist, so the breadcrumbs always lead back to the docs home.
 */
export function ReaderPageFrame({ children }: ReaderPageFrameProps) {
  const getTechdocsRootPath = useRouteRef(createTechdocsRootRef);
  const readerDocType = useReaderDocType();
  const { entityRef } = useTechDocsReaderPage();
  const { entity: siteEntity, loading: siteLoading } = useReaderEntity();
  const { subDocEntity, loading: subDocLoading } =
    useReaderSubDocEntity(siteEntity);
  // The header describes the open page: the doc entity of an ADR or a
  // playbook page, else the entity of the docs site. While the doc entity
  // loads, the header shows neither, so that it does not change twice.
  const entity = subDocLoading ? undefined : (subDocEntity ?? siteEntity);
  const loading = siteLoading || subDocLoading;
  const { primaryTitle } = useEntityPresentation(entity ?? entityRef);
  const siteTitle = getSiteTitle(
    entityRef,
    primaryTitle,
    (siteLoading && !siteEntity) || subDocLoading,
  );
  const tabTitle = useTabTitle(siteTitle);
  useReaderTitleFromMetadata();
  const legacyTrail = useLegacyNavTrail();
  // An exception to PAGE-004: the docs sidebar shows the place of a legacy
  // page, and a nav group has no page of its own to go back to.
  const hasBackLink = !isLegacyDocsEntity(entityRef);

  const docsRootPath = getTechdocsRootPath();
  const breadcrumbs: HeaderBreadcrumb[] = [
    { label: 'Documentation', href: docsRootPath },
  ];
  if (readerDocType) {
    breadcrumbs.push({
      label: DOC_TYPES[readerDocType].title,
      href: getCategoryPath(docsRootPath, readerDocType),
    });
  }
  // The legacy site is 1 entity for many pages. On a page below its home, the
  // title is the nav label of the page, and the breadcrumbs lead through the
  // site and the nav groups above the page.
  const legacyPage = legacyTrail?.nodes[legacyTrail.nodes.length - 1];
  if (legacyTrail && legacyPage) {
    breadcrumbs.push(
      { label: siteTitle, href: legacyTrail.basePath },
      ...getLegacyGroupBreadcrumbs(
        legacyTrail.nodes.slice(0, -1),
        legacyTrail.basePath,
      ),
    );
  }

  // An empty list, not `undefined`: PluginPage falls back to the owner of the
  // plugin, which is the Backstage TechDocs plugin here.
  const ownerEntityRefs = entity
    ? getEntityRelations(entity, RELATION_OWNED_BY).map(ref =>
        stringifyEntityRef(ref),
      )
    : [];
  const sourceUrl = getSourceUrl(siteEntity);

  return (
    <>
      <Helmet titleTemplate="%s">
        <title>{tabTitle}</title>
      </Helmet>
      {/* Full width, not the default 1200px column: the docs sidebar sits on
          the left, and the fixed table of contents of TechDocs sits at the
          right edge of the window. The docs index page uses the same width,
          so that the sidebar does not move when a doc opens. */}
      <PluginPage
        width="full"
        title={legacyPage?.label ?? siteTitle}
        breadcrumbs={breadcrumbs}
        ownerEntityRefs={ownerEntityRefs}
        metadata={getHeaderMetadata(entity, ownerEntityRefs, loading)}
        customActions={
          sourceUrl ? (
            <ButtonLink
              href={sourceUrl}
              target="_blank"
              rel="noreferrer"
              variant="secondary"
              iconStart={<RiCodeLine />}
              iconEnd={<RiExternalLinkLine />}
            >
              View source
            </ButtonLink>
          ) : undefined
        }
      >
        <DocsLayout>
          {hasBackLink && (
            <DocsBackLink to={breadcrumbs[breadcrumbs.length - 1]} />
          )}
          {children}
        </DocsLayout>
      </PluginPage>
    </>
  );
}

/**
 * The header details of the doc. Reading time and the update date are
 * annotations of the docs entity. The legacy site and a parent component with
 * ADRs or playbooks are 1 entity for many pages, so their values describe the
 * whole site, not the open page. Only a category doc shows them.
 *
 * While the entity loads, the Owner row shows a skeleton. Without the row,
 * the header grows when the entity arrives, and the page moves down.
 */
function getHeaderMetadata(
  entity: Entity | undefined,
  ownerEntityRefs: string[],
  loading: boolean,
): HeaderMetadataItem[] {
  const items: HeaderMetadataItem[] = [];

  if (!entity && loading) {
    items.push({
      label: 'Owner',
      value: <Skeleton width={120} height={16} />,
    });
  } else if (ownerEntityRefs.length > 0) {
    items.push({
      label: 'Owner',
      value: (
        <EntityRefLinks entityRefs={ownerEntityRefs} defaultKind="group" />
      ),
    });
  }

  if (!entity || !isCategoryDocEntity(entity)) {
    return items;
  }

  const annotations = entity.metadata.annotations;
  const updated = parseDocsUpdateDate(
    annotations?.[DOCS_LAST_UPDATE_ANNOTATION],
  );
  items.push(
    {
      label: 'Reading time',
      value: annotations?.[LENGTH_ANNOTATION] ?? EN_DASH,
    },
    {
      label: 'Updated',
      // The software-catalogue writes the date, so it is a backend event and
      // shows in UTC.
      value: updated ? (
        <Timestamp timestamp={updated} displayTimezone="UTC" />
      ) : (
        EN_DASH
      ),
    },
  );

  return items;
}

/**
 * The doc entity of the open page when the page lives in the docs site of
 * another entity: an ADR or a component playbook in the site of its parent
 * component. Only the ADR and playbook folders hold such pages, so the hook
 * looks up no other page. `undefined` when no doc entity points to the page.
 */
function useReaderSubDocEntity(siteEntity: Entity | undefined): {
  subDocEntity: Entity | undefined;
  loading: boolean;
} {
  const { entityRef } = useTechDocsReaderPage();
  const { '*': path = '' } = useParams();
  const isSubDocPage =
    !!siteEntity &&
    !isCategoryDocEntity(siteEntity) &&
    resolveDocType(siteEntity, path) !== undefined;
  const { data, isPending } = useSubDocEntity(entityRef, path, {
    enabled: isSubDocPage,
  });

  return {
    subDocEntity: data ?? undefined,
    loading: isSubDocPage && isPending,
  };
}

/**
 * The title of the docs site. The legacy site has a fixed name. Another site
 * has no title while its entity loads: the page does not know the category of
 * the doc yet, and the URL slug shows where the category breadcrumb then
 * appears.
 */
function getSiteTitle(
  entityRef: CompoundEntityRef,
  primaryTitle: string,
  entityLoading: boolean,
): string {
  if (isLegacyDocsEntity(entityRef)) {
    return LEGACY_DOCS_TITLE;
  }
  return entityLoading ? '' : primaryTitle;
}

/**
 * The nav items from the top of the legacy site nav down to the open page,
 * and the URL of the site. `undefined` outside the site, on the site home, and
 * for a page that is not in the nav.
 */
function useLegacyNavTrail():
  | { nodes: NavNode[]; basePath: string }
  | undefined {
  const { basePath, pagePath } = useLegacyDocsLocation();
  const { data: nav } = useLegacyDocsNav();

  if (!pagePath || !nav) {
    return undefined;
  }
  const nodes = findNavTrail(nav, pagePath);
  return nodes && { nodes, basePath };
}

/** 1 breadcrumb per nav group. A group with no page at all has none. */
function getLegacyGroupBreadcrumbs(
  groups: NavNode[],
  basePath: string,
): HeaderBreadcrumb[] {
  return groups.flatMap(group => {
    const pagePath = getFirstPagePath(group);
    return pagePath === undefined
      ? []
      : [{ label: group.label, href: `${basePath}/${pagePath}` }];
  });
}

/** The repository link of the doc. A local `dir` or `file` source has none. */
function getSourceUrl(
  entity: TechDocsEntityMetadata | undefined,
): string | undefined {
  const location = entity?.locationMetadata;
  if (!location || location.type === 'dir' || location.type === 'file') {
    return undefined;
  }
  return location.target;
}

/**
 * The browser tab title: the app title, the doc title, and the first 3 path
 * segments of the open page.
 */
function useTabTitle(docTitle: string): string {
  const configApi = useApi(configApiRef);
  const { '*': path = '' } = useParams();

  const appTitle = configApi.getOptionalString('app.title') || 'Backstage';
  const pathTitles = path
    .replace(/\/$/, '')
    .split('/')
    .filter(Boolean)
    .slice(0, 3)
    .map(segment =>
      segment.replace(/-/g, ' ').split(' ').map(capitalize).join(' '),
    );

  return [appTitle, docTitle, ...pathTitles].filter(Boolean).join(' | ');
}

/**
 * Gives the reader context the site name and description of the mkdocs build,
 * like the header of the Backstage TechDocs plugin does. The reader parts of
 * that plugin read them from the context.
 */
function useReaderTitleFromMetadata() {
  const {
    setTitle,
    setSubtitle,
    metadata: { value: metadata },
  } = useTechDocsReaderPage();

  useEffect(() => {
    if (!metadata) return;
    setTitle(metadata.site_name);
    setSubtitle(() => {
      const { site_description } = metadata;
      return !site_description || site_description === 'None'
        ? ''
        : site_description;
    });
  }, [metadata, setTitle, setSubtitle]);
}
